import { getCurrencySymbol, AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, LOCALE_ID, ChangeDetectorRef, inject, input, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ChargeMasterService, CommonService, SalesReturnService, ToastMessageService, PaymentService } from '@fitness-force/api';
import { AssignedToComponent } from '@fitness-force/common/assigned-to';
import { PayModeLevel1, RefundMode, SalesReturnReasons } from '@fitness-force/enum';
import { ErrorMsgComponent } from '@fitness-force/errors';;
import type { ChargeMaster, Claim, InvoiceById, SaleReturnCalculationRequest, SalesReturnLineItemRequest, SalesReturnServices, ServiceTypeResponse } from '@fitness-force/models';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { COUNTRY_MASTER_STATE, SERVICETYPE_LIST_STATE } from '@fitness-force/state';
import { InputNumberComponent } from '@fitness-force/common-input-number';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { PopoverModule } from 'primeng/popover';
import { TableModule } from 'primeng/table';
import { BehaviorSubject, Observable, catchError, retryWhen, scan, switchMap, throwError, timer } from 'rxjs';
import { StaffStoreService } from '@fitness-force/api';
import { ProductSkuService } from '@fitness-force/retail-billing';

@Component({
  selector: 'create-sales-return',
  templateUrl: './create-sales-return.component.html',
  styleUrls: ['./create-sales-return.component.scss'],
  providers:[],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    TableModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    CurrencyformatPipe,
    MessageModule,
    CheckboxModule,
    AssignedToComponent,
    ErrorMsgComponent,
    InputNumberComponent,
    PopoverModule,
    TranslateModule,
    AsyncPipe,
    NgClass
]
})

export class CreateSalesReturnComponent implements OnInit {
  private store = inject(Store);
  translate = inject(TranslateService);
  private fb = inject(UntypedFormBuilder);
  private chargeMasterService = inject(ChargeMasterService);
  private commonService = inject(CommonService);
  private salesReturnService = inject(SalesReturnService);
  private toastMessageService = inject(ToastMessageService);
  private cdr = inject(ChangeDetectorRef);
  private staffStoreService = inject(StaffStoreService);
  private paymentService = inject(PaymentService);
  private productSkuService = inject(ProductSkuService);

  readonly showRefundModal = input<boolean>(false, { alias: "visible" });
  readonly invoiceData = input.required<InvoiceById>();
  readonly invoiceId = input.required<number>();
  readonly closeDialog = output<{
    closePopup: boolean;
    reloadData: boolean;
}>();

  claims: string[] = ['can_do_billing'];
  claim$: Observable<Claim[]> = new Observable();
  clientSalesReturnForm: UntypedFormGroup = new UntypedFormGroup({});
  serviceCategories:ServiceTypeResponse[] = [] as ServiceTypeResponse[];
  chargeMasterList:ChargeMaster[] =[] as ChargeMaster[];
  salesReturnServices$ = new BehaviorSubject<SalesReturnServices[]>([]); //: SalesReturnService[] = [];
  currencySymbol$ = new BehaviorSubject<string>('');

  returnReasons = Object.entries(SalesReturnReasons).filter((key) => typeof key[1] != 'number').map(([key, value]) => ({ key, value }));

  chooseRefundPaymentMethods  = new BehaviorSubject(false);
  isClientRepresentative = new BehaviorSubject(true);
  
  salesReturnAmount:number=0;
  btnLoading= new BehaviorSubject(false);
  refundCalculationData:{total_invoice_amt:number,total_payment_received:number,total_sales_return:number,excess_amt_to_return:string}[]=[];
  TableLoader$ = new BehaviorSubject(false);
  refundConfirmModal:boolean=false;
  refundConfirmControl = new UntypedFormControl('');
  isAvailableOnlineRefundResponse:any;
  public locale: string = inject(LOCALE_ID);

  productSkus: any[] = [];
  skuReturnReasons: any[] = [];

  ngOnInit(): void {
    this.clientSalesReturnForm = this.fb.group({});
    this.TableLoader$.next(true);
    if(this.invoiceData().invoice_line_item.length > 0 && this.invoiceData().invoice_line_item.find(o => o.type.toLowerCase() === 'product')){
      this.getProductSkus();
      this.getSkuReturnReasons();
    }
    else{
      this.ListChargeMaster();
    }
    this.getAvailableOnlineRefund();
    this.store.select(COUNTRY_MASTER_STATE).subscribe((country) => {
      this.currencySymbol$.next(getCurrencySymbol(
        country.currency_code,
        'narrow',
        this.locale
      ));
    });
    this.store.select(SERVICETYPE_LIST_STATE).subscribe(
      {
        next: (res: ServiceTypeResponse[]) => {
          if (res && res.length > 0) {
            this.serviceCategories = res;
          }
        }
      })
    this.createDynamicForm();
  }

  ListChargeMaster() {
    this.chargeMasterService.getChargeMasters(this.invoiceData().tenant_id).subscribe({
      next: (chargeList: any) => {
        this.chargeMasterList = chargeList.items as ChargeMaster[];
        this.createDynamicForm();
        this.disableControls();
      }
    });
  }

  getProductSkus() {
    this.productSkuService.getTenantSkusWithInventory(this.invoiceData().tenant_id,1,9999,true).subscribe({
      next: (responose: any) => {
        this.productSkus = responose.items || [];
        this.ListChargeMaster();
      }
    });
  }

  getSkuReturnReasons() {
    this.productSkuService.getSkuReturnReasons().subscribe({
      next: (responose: any) => {
        this.skuReturnReasons = responose.items ? responose.items.filter((item: any) => item.is_active) : [];
      }
    });
  }

  getAvailableOnlineRefund() {
    this.salesReturnService.GetAvailableOnlineRefund(this.invoiceData().tenant_id,this.invoiceData().client_id,this.invoiceId()).subscribe({
      next: (responose: any) => {
        this.isAvailableOnlineRefundResponse = responose;
        if(this.isAvailableOnlineRefundResponse.isOnlineRefundable)
        {
          this.paymentService.getPaymentByPaymentId(this.invoiceData().client_id,this.isAvailableOnlineRefundResponse.onlineRefundPaymentId).subscribe({
            next: (payment: any) => {
              this.isAvailableOnlineRefundResponse.payment_method = payment.payment_method;
              this.chooseRefundPaymentMethods.next(true);
              this.cdr.detectChanges();
            },
            error: () => {
              this.chooseRefundPaymentMethods.next(true);
              this.cdr.detectChanges();
            }
          });
        }
      }
    });
  }

  createDynamicForm()
  {
    this.TableLoader$.next(true);
    const group = this.fb.group({});
    const services:SalesReturnServices[] = [];
    this.invoiceData().invoice_line_item.forEach(invoice => {
      if(this.serviceCategories && this.serviceCategories.length > 0 && this.serviceCategories.find(o => o.id == invoice.service_category_id))
      {
        const service= this.serviceCategories.find(o => o.id == invoice.service_category_id);
        const salesReturnService: SalesReturnServices ={ 
          invoice_line_id : invoice.id,
          name_primary_language : service?.name_primary_language || '',
          name_secondary_language : service?.name_secondary_language || '',
          invoice_amount : Math.max(0,invoice.total_amount_in_minor_currency - invoice.total_sales_return_amount),
          quantity : invoice.quantity -invoice.total_sales_return_quantity,
          sales_return_amount : 0,
          is_total_column : false,
          has_tax_cell: false,
          has_total_cell: false,
          type : invoice.type,
          description:""
        };
        services.push(salesReturnService);
      }
    })
    this.invoiceData().invoice_line_item.forEach(invoice => {
      if(this.chargeMasterList && this.chargeMasterList.length > 0 && this.chargeMasterList.find(o => o.id == invoice.charge_category_id))
      {
        const chargeCategory= this.chargeMasterList.find(o => o.id == invoice.charge_category_id);
        const salesReturnService: SalesReturnServices ={ 
          invoice_line_id : invoice.id,
          name_primary_language : chargeCategory?.name_primary_language || '',
          name_secondary_language : chargeCategory?.name_secondary_language || '',
          invoice_amount : Math.max(0,invoice.total_amount_in_minor_currency - invoice.total_sales_return_amount),
          quantity : invoice.quantity -invoice.total_sales_return_quantity,
          sales_return_amount : 0,
          is_total_column : false,
          has_tax_cell: false,
          has_total_cell: false,
          type : invoice.type,
          description:""
        };
        services.push(salesReturnService);
      }
    })

    this.invoiceData().invoice_line_item.forEach(invoice => {
      if(this.productSkus && this.productSkus.length > 0 && this.productSkus.find(o => o.id == invoice.sku_id))
      {
        const productSku= this.productSkus.find(o => o.id == invoice.sku_id);
        const salesReturnService: SalesReturnServices ={ 
          invoice_line_id : invoice.id,
          name_primary_language : productSku?.sku_name_primary_language || '',
          name_secondary_language : productSku?.sku_name_secondary_language || '',
          invoice_amount : Math.max(0,invoice.total_amount_in_minor_currency - invoice.total_sales_return_amount),
          quantity : invoice.quantity -invoice.total_sales_return_quantity,
          sales_return_amount : 0,
          is_total_column : false,
          has_tax_cell: false,
          has_total_cell: false,
          type : invoice.type,
          description:"",
          sku_return_reason_id: 0
        };
        services.push(salesReturnService);
      }
    })

    services.forEach((control) => {
      group.addControl('service_quantity_' + control.invoice_line_id,new UntypedFormControl(control.type =='Service' ? control.quantity : 0, [Validators.max(control.quantity)]));
      group.addControl('service_sales_return_amount_' + control.invoice_line_id,new UntypedFormControl('', [Validators.max(this.commonService.ConvertAmountFromMinorCurrency(control.invoice_amount))]));
      group.addControl('service_description_' + control.invoice_line_id,new UntypedFormControl(control.description, []));
      group.addControl('service_sku_return_reason_' + control.invoice_line_id,new UntypedFormControl(control.sku_return_reason_id, []));
    });

    const defaultService: SalesReturnServices = {
      invoice_line_id : 0,
      name_primary_language : "Total",
      name_secondary_language : "Total",
      invoice_amount : 0,
      quantity : 0,
      sales_return_amount : 0,
      is_total_column : true,
      has_tax_cell: false,
      has_total_cell: true,
      type : "",
      description:""
    };

    services.push(defaultService);
    this.salesReturnServices$.next(services);
    group.addControl('comment',new UntypedFormControl('', [Validators.required]));
    group.addControl('reason',new UntypedFormControl('', [Validators.required]));
    group.addControl('sales_rep',new UntypedFormControl( '', [Validators.required]));
    this.clientSalesReturnForm = group;
    this.refundCalculationData =[];
    this.refundCalculationData.push({total_invoice_amt:this.invoiceData().total_amount_in_minor_currency,total_payment_received:this.invoiceData().total_payment_in_minor_currency,total_sales_return:this.invoiceData().total_sales_return_in_minor_currency,excess_amt_to_return: "0"})
    this.TableLoader$.next(false);
    this.disableControls();
  }

  disableControls()
  {
    this.salesReturnServices$.getValue().forEach(service => {
      if(service.type =='Service')
      {
        this.getSalesReturnFormControl('service_quantity_' + service.invoice_line_id).disable();
      }
    });
    this.staffStoreService.getStaffById(this.invoiceData().sales_representative).subscribe((staff) => {
      this.SalesRepFormControl.setValue(staff);
    });
    // this.store.select(STAFF_STATE).subscribe((staff: any) => {
    //   const defaultStaff = staff.staffListAll.find(
    //     (s: any) => this.invoiceData.sales_representative == s.id
    //   );
    //   this.SalesRepFormControl.setValue(defaultStaff);
    // });
  }


  getSalesCalculation()
  {
      this.TableLoader$.next(true);
      const invalidControls: string[] = [];
      this.salesReturnServices$.getValue().forEach(service => {
        if( this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id) && !this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).valid)
           invalidControls.push('service_sales_return_amount_' + service.invoice_line_id);
      });
      if(invalidControls.length > 0) return;

      let saleReturnCalculationRequest : SaleReturnCalculationRequest = {} as SaleReturnCalculationRequest;
      saleReturnCalculationRequest = this.getSalesReturnCalculationRequest();
      this.salesReturnService.SalesReturnCalculation(this.invoiceData().tenant_id,this.invoiceData().client_id,this.invoiceId(),saleReturnCalculationRequest)
      .pipe(
        retryWhen(errors =>
          errors.pipe(
            // Check for the specific error condition that should trigger a retry
            scan((count, error) => {
              if (
                count < 2 && // Retry only 2 times
                error?.originalError.error?.[0] &&
                error.originalError.error[0].errorCode === 'LessThanOrEqualValidator'
              ) {
                // Update the payload before retrying
                saleReturnCalculationRequest.sales_return_line_items.forEach(o => {
                  o.total_amount_in_minor_currency = o.rounded_total_amount_in_minor_currency!;
                });
                return count + 1; // Increment the retry count
              }
              // Do not retry for other errors or after 3 retries
              throw error;
            }, 0),
            switchMap(() => timer(300))
          )
        ),
        catchError((error) => {
          return throwError(error);
        })
      ).subscribe({
        next:async (responose:any)=>{
         this.salesReturnAmount = responose.amount;
         this.refundCalculationData =[];
         this.refundCalculationData.push({total_invoice_amt:this.invoiceData().total_amount_in_minor_currency,total_payment_received:this.invoiceData().total_payment_in_minor_currency,total_sales_return:this.invoiceData().total_sales_return_in_minor_currency,excess_amt_to_return:responose.amount})
         this.TableLoader$.next(false);
        },
        error:(error:any)=>{
          this.TableLoader$.next(false);
          this.handleError(error);
        }
      })
  }

  getSalesReturnCalculationRequest()
  {
    const saleReturnCalculationRequest : SaleReturnCalculationRequest ={} as SaleReturnCalculationRequest;
    saleReturnCalculationRequest.sales_rep = this.SalesRepFormControl.value.id;
    saleReturnCalculationRequest.return_reason = this.SalesReturnReasonFormControl.value.key;
    saleReturnCalculationRequest.return_comments = this.SalesReturnReasonCommentFormControl.value;
    saleReturnCalculationRequest.sales_return_date = new Date().toISOString()
    saleReturnCalculationRequest.sales_return_line_items=[];
    this.salesReturnServices$.value.forEach(service => {
      if((service.type.toString() === "Service") && this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value > 0)
      {
        const SalesReturnLineItem: SalesReturnLineItemRequest ={
          invoice_line_id: service.invoice_line_id,
          description:  this.getSalesReturnFormControl('service_description_' + service.invoice_line_id).value,
          quantity : 0,
          total_amount_in_minor_currency : this.commonService.ConvertMinorCurrencyFromAmount(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
          rounded_total_amount_in_minor_currency: this.commonService.ConvertMinorCurrencyFromAmountRounded(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
          sku_return_reason_id: 0
        };
        saleReturnCalculationRequest.sales_return_line_items.push(SalesReturnLineItem);
      }
      if((service.type.toString() === "Charge") 
         && (this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value > 0 || this.getSalesReturnFormControl('service_quantity_' + service.invoice_line_id).value > 0))
      {
        const SalesReturnLineItem: SalesReturnLineItemRequest = {
          invoice_line_id: service.invoice_line_id,
          description:  this.getSalesReturnFormControl('service_description_' + service.invoice_line_id).value,
          quantity : this.getSalesReturnFormControl('service_quantity_' + service.invoice_line_id).value,
          total_amount_in_minor_currency : this.commonService.ConvertMinorCurrencyFromAmount(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
          rounded_total_amount_in_minor_currency: this.commonService.ConvertMinorCurrencyFromAmountRounded(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
          sku_return_reason_id: 0
        };
        saleReturnCalculationRequest.sales_return_line_items.push(SalesReturnLineItem);
      }
      if((service.type.toString() === "Product") 
        && (this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value > 0 || this.getSalesReturnFormControl('service_quantity_' + service.invoice_line_id).value > 0))
      {
        const SalesReturnLineItem: SalesReturnLineItemRequest = {
          invoice_line_id: service.invoice_line_id,
          description:  this.getSalesReturnFormControl('service_description_' + service.invoice_line_id).value,
          quantity : this.getSalesReturnFormControl('service_quantity_' + service.invoice_line_id).value,
          total_amount_in_minor_currency : this.commonService.ConvertMinorCurrencyFromAmount(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
          rounded_total_amount_in_minor_currency: this.commonService.ConvertMinorCurrencyFromAmountRounded(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
          sku_return_reason_id: this.getSalesReturnFormControl('service_sku_return_reason_' + service.invoice_line_id).value ? this.getSalesReturnFormControl('service_sku_return_reason_' + service.invoice_line_id).value : 0
        };
        saleReturnCalculationRequest.sales_return_line_items.push(SalesReturnLineItem);
      }
   });
   return saleReturnCalculationRequest;
  }

  ProcessSalesReturn(mode:string)
  {
    this.btnLoading.next(true);
    this.clientSalesReturnForm.markAllAsTouched();
    this.refundConfirmControl.markAllAsTouched();
    if(this.clientSalesReturnForm.valid && this.refundConfirmControl.valid){
      const saleReturnCalculationRequest : SaleReturnCalculationRequest ={} as SaleReturnCalculationRequest;
      saleReturnCalculationRequest.sales_rep = this.SalesRepFormControl.value.id;
      saleReturnCalculationRequest.return_reason = this.SalesReturnReasonFormControl.value.key;
      saleReturnCalculationRequest.return_comments = this.SalesReturnReasonCommentFormControl.value;
      saleReturnCalculationRequest.sales_return_date = new Date().toISOString()
      saleReturnCalculationRequest.sales_return_line_items=[];
        this.salesReturnServices$.getValue().forEach(service => {
          if((service.type.toString() === "Service") && this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value > 0)
          {
            const SalesReturnLineItem: SalesReturnLineItemRequest ={
              invoice_line_id: service.invoice_line_id,
              description:  this.getSalesReturnFormControl('service_description_' + service.invoice_line_id).value,
              quantity : 0,
              total_amount_in_minor_currency : this.commonService.ConvertMinorCurrencyFromAmount(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
              rounded_total_amount_in_minor_currency: this.commonService.ConvertMinorCurrencyFromAmountRounded(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
              sku_return_reason_id: 0
            };
            saleReturnCalculationRequest.sales_return_line_items.push(SalesReturnLineItem);
          }
          if((service.type.toString() === "Charge") 
                && (this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value > 0 
                || this.getSalesReturnFormControl('service_quantity_' + service.invoice_line_id).value > 0))
          {
            const SalesReturnLineItem: SalesReturnLineItemRequest = {
              invoice_line_id: service.invoice_line_id,
              description:  this.getSalesReturnFormControl('service_description_' + service.invoice_line_id).value,
              quantity : this.getSalesReturnFormControl('service_quantity_' + service.invoice_line_id).value,
              total_amount_in_minor_currency : this.commonService.ConvertMinorCurrencyFromAmount(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
              rounded_total_amount_in_minor_currency: this.commonService.ConvertMinorCurrencyFromAmountRounded(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
              sku_return_reason_id: 0
            };
            saleReturnCalculationRequest.sales_return_line_items.push(SalesReturnLineItem);
          }
          if((service.type.toString() === "Product") 
            && (this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value > 0 || this.getSalesReturnFormControl('service_quantity_' + service.invoice_line_id).value > 0))
          {
            const SalesReturnLineItem: SalesReturnLineItemRequest = {
              invoice_line_id: service.invoice_line_id,
              description:  this.getSalesReturnFormControl('service_description_' + service.invoice_line_id).value,
              quantity : this.getSalesReturnFormControl('service_quantity_' + service.invoice_line_id).value,
              total_amount_in_minor_currency : this.commonService.ConvertMinorCurrencyFromAmount(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
              rounded_total_amount_in_minor_currency: this.commonService.ConvertMinorCurrencyFromAmountRounded(this.getSalesReturnFormControl('service_sales_return_amount_' + service.invoice_line_id).value),
              sku_return_reason_id: this.getSalesReturnFormControl('service_sku_return_reason_' + service.invoice_line_id).value ? this.getSalesReturnFormControl('service_sku_return_reason_' + service.invoice_line_id).value.id : 0
            };
            saleReturnCalculationRequest.sales_return_line_items.push(SalesReturnLineItem);
          }
      });

      if(mode == 'offline')
      {
        saleReturnCalculationRequest.refund_mode = RefundMode.Offline;
        saleReturnCalculationRequest.online_refund_payment_id = 0;
      }
      else if(mode == 'credit')
      {
        saleReturnCalculationRequest.refund_mode = RefundMode.Credit;
        saleReturnCalculationRequest.online_refund_payment_id = 0;
      }
      else{
        saleReturnCalculationRequest.refund_mode = RefundMode.Online;
        saleReturnCalculationRequest.online_refund_payment_id = this.isAvailableOnlineRefundResponse.onlineRefundPaymentId;
      }
      this.salesReturnService.CreateSalesReturn(this.invoiceData().tenant_id,this.invoiceData().client_id,this.invoiceId(),saleReturnCalculationRequest)
      .pipe(
        retryWhen(errors =>
          errors.pipe(
            // Check for the specific error condition that should trigger a retry
            scan((count, error) => {
              if (
                count < 2 && // Retry only 2 times
                error?.originalError.error?.[0] &&
                error.originalError.error[0].errorCode === 'LessThanOrEqualValidator'
              ) {
                // Update the payload before retrying
                saleReturnCalculationRequest.sales_return_line_items.forEach(o => {
                  o.total_amount_in_minor_currency = o.rounded_total_amount_in_minor_currency!;
                });
                return count + 1; // Increment the retry count
              }
              // Do not retry for other errors or after 3 retries
              throw error;
            }, 0),
            switchMap(() => timer(300))
          )
        ),
        catchError((error) => {
          return throwError(error);
        })
      ).subscribe({
        next:()=>{
         this.translate.get('SALES_RETURN.MSG.REFUND_PROCESED')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
              this.btnLoading.next(false);
              this.closePopup(true);
        },
        error:(error:any)=>{
          this.btnLoading.next(false);
          this.handleError(error);
        }
      })
    }
    else{
        Object.keys(this.clientSalesReturnForm.controls).forEach((field) => {
          const control = this.clientSalesReturnForm.get(field);
          control?.markAsDirty({ onlySelf: true });
        });
        this.btnLoading.next(false);
    }
  }

  handleError(error: any) {
    this.toastMessageService.showError(error);
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }


  closePopup(event:boolean)
  {
    this.closeDialog.emit({closePopup: true, reloadData: false});
    this.refundConfirmModal = false;
    this.refundConfirmControl.reset();
    this.closeDialog.emit({closePopup:true, reloadData:event});
  }

  get getRefundAmount():number
  {
    return this.commonService.ConvertAmountFromMinorCurrency(this.getSalesReturnAmountTotal);
  }

  refundToEwallet()
  {
    this.chooseRefundPaymentMethods.next(true);
  }

  refundBackToSource()
  {
    this.refundConfirmControl.reset();
    this.refundConfirmControl.setValidators([Validators.required, Validators.pattern('^REFUND$')]);
    this.refundConfirmControl.updateValueAndValidity();
    this.refundConfirmModal = true;
  }

  offlineRefund()
  {
    this.chooseRefundPaymentMethods.next(true);
  }

  get PaymentMethodDetails() {
    if(!this.isAvailableOnlineRefundResponse.payment_method)
    {
      return null;
    }
    const paymentData: any = {};
    const data = PayModeLevel1[this.isAvailableOnlineRefundResponse.payment_method.level1.toString() as keyof typeof PayModeLevel1]
    switch (data) {
      case PayModeLevel1.Cash:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.ACH:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.Adjustment:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.Card:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: this.isAvailableOnlineRefundResponse.payment_method.masked_card_number })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });

        break;
      case PayModeLevel1.Cheque:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.Emandate:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.NetBanking:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: this.isAvailableOnlineRefundResponse.payment_method.masked_account_number ? this.isAvailableOnlineRefundResponse.payment_method.masked_account_number : "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.UPI:
        paymentData.number = this.isAvailableOnlineRefundResponse.payment_method.vpa_id;
        break;
      case PayModeLevel1.Wallet:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.Other:
        this.translate
          .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: "****" })
          .subscribe((translations: any) => {
            paymentData.number = translations;
          });
        break;
      case PayModeLevel1.PayTo:
        paymentData.number = this.isAvailableOnlineRefundResponse.payment_method.pay_to_details;
        break;
    }
    return paymentData;
  }

  get getInvoiceTotalAmount():number
  {
    return this.salesReturnServices$.getValue().reduce(function (acc, obj) {
      return acc + obj.invoice_amount;
    }, 0);
  }

  get getSalesReturnAmountTotal(): number
  {
    let amount:number=0;
    this.salesReturnServices$.getValue().forEach(service => {
      if(this.clientSalesReturnForm.controls['service_sales_return_amount_' + service.invoice_line_id] && this.clientSalesReturnForm.controls['service_sales_return_amount_' + service.invoice_line_id].valid && this.clientSalesReturnForm.controls['service_sales_return_amount_' + service.invoice_line_id].value)
        amount = amount + this.commonService.ConvertMinorCurrencyFromAmount(Math.max(0,this.clientSalesReturnForm.controls['service_sales_return_amount_' + service.invoice_line_id].value))
    });
    return amount;
  }
  

  get SalesReturnReasonFormControl(): UntypedFormControl {
    return this.clientSalesReturnForm.get('reason') as UntypedFormControl;
  }

  get SalesReturnReasonCommentFormControl(): UntypedFormControl {
    return this.clientSalesReturnForm.get('comment') as UntypedFormControl;
  }

  getSalesReturnFormControl(controlName:string): UntypedFormControl {
    return this.clientSalesReturnForm.get(controlName) as UntypedFormControl;
  }

  get SalesRepFormControl(): UntypedFormControl
  {
    return this.clientSalesReturnForm.get('sales_rep') as UntypedFormControl;
  }

  salesReturnAmountInCurrency():number
  {
    return this.commonService.ConvertAmountFromMinorCurrency(this.salesReturnAmount);
  }

}
